import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Code, 
  Database, 
  Cloud, 
  GitBranch, 
  Layout, 
  ScanLine, 
  Shield, 
  Settings,
  Wrench,
  Smartphone,
  Palette
} from 'lucide-react';

const iconMap = {
  code: Code,
  database: Database,
  layout: Layout,
  cloud: Cloud,
  'git-branch': GitBranch,
  smartphone: Smartphone,
  palette: Palette,
  scan: ScanLine,
  shield: Shield,
  wrench: Wrench,
  tool: Settings
};

const SkillSection = ({ category, index }) => {
  const [hoveredSkill, setHoveredSkill] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);

  React.useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    };
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-neutral-200 dark:border-neutral-800 hover:border-primary-300 dark:hover:border-primary-700 hover:shadow-lg dark:hover:shadow-primary-900/20 transition-all"
    >
      <h3 className="text-xl font-bold text-neutral-900 dark:text-neutral-50 mb-6 pb-3 border-b border-neutral-200 dark:border-neutral-800">
        {category.name}
      </h3>
      <div className="space-y-3">
        {category.skills.map((skill, skillIndex) => {
          const IconComponent = iconMap[skill.icon] || Code;
          const isHovered = hoveredSkill === skillIndex;
          
          return (
            <motion.div
              key={skillIndex}
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: (index * 0.1) + (skillIndex * 0.05) }}
              onMouseEnter={() => setHoveredSkill(skillIndex)}
              onMouseLeave={() => setHoveredSkill(null)}
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#87CEEB]/20 dark:hover:bg-[#87CEEB]/10 transition-all cursor-pointer group"
            >
              {skill.logo ? (
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 relative overflow-hidden"
                  style={{
                    backgroundColor: isHovered ? skill.color : 'transparent',
                    boxShadow: isHovered ? `0 4px 20px ${skill.color}40` : 'none'
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <img 
                    src={skill.logo} 
                    alt={skill.name}
                    className="w-7 h-7 object-contain relative z-10 transition-all duration-300"
                    style={{
                      filter: isHovered 
                        ? 'brightness(0) invert(1)' 
                        : (isDarkMode && skill.name === 'Git & GitHub' ? 'brightness(0) invert(1)' : 'none')
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-7 h-7 hidden items-center justify-center">
                    <IconComponent className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                </div>
              ) : (
                <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center group-hover:bg-primary-600 dark:group-hover:bg-primary-500 transition-colors">
                  <IconComponent className="w-5 h-5 text-primary-600 dark:text-primary-400 group-hover:text-white transition-colors" />
                </div>
              )}
              <span className="text-neutral-700 dark:text-neutral-300 font-medium text-sm group-hover:text-neutral-900 dark:group-hover:text-white transition-colors">{skill.name}</span>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
};

export default SkillSection;
